import { useTranslation } from 'react-i18next';
import { toast } from '@qodo/design-system';
import { OIDC_PROVIDER } from 'network/httpClients/OIDCAuthenticationClient/consts';
import { QUERY_PARAMS } from 'routes/consts';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { buildMixpanelErrorData } from 'services/mixpanel/utils';
import {
  handleOIDCError,
  isOIDCError,
  fetchAuthorizeEndpoint,
} from 'utils/authentication';
import { isAirgappedMode } from 'utils/env';
import {
  generateCodeChallengeS256,
  generateCodeVerifier,
  PKCE_CODE_CHALLENGE_METHOD,
  setPkceCodeVerifier,
} from 'utils/pkce';
import { getSSOProviderParams, getSSOOptionalParams } from '../utils';
import { EmailInputForm } from './EmailInputForm';

export const SSO = () => {
  const { t } = useTranslation();

  const onSubmit = async (email: string) => {
    try {
      const additionalParams = new URLSearchParams({
        ...Object.fromEntries(getSSOProviderParams(email)),
        ...getSSOOptionalParams(),
      });

      // AIRGAPPED ONLY: Zitadel/Keycloak deployments may require PKCE(best practice)
      // We intentionally scope PKCE to the airgapped SSO flow only.
      if (isAirgappedMode()) {
        const codeVerifier = generateCodeVerifier(128);
        const codeChallenge = await generateCodeChallengeS256(codeVerifier);

        setPkceCodeVerifier(codeVerifier);

        additionalParams.set(QUERY_PARAMS.CODE_CHALLENGE, codeChallenge);
        additionalParams.set(
          QUERY_PARAMS.CODE_CHALLENGE_METHOD,
          PKCE_CODE_CHALLENGE_METHOD,
        );
      }

      const response = await fetchAuthorizeEndpoint(additionalParams, {
        redirect: 'manual',
      });

      if (response.type === 'opaqueredirect') {
        window.location.href = response.url;
      }
    } catch (error) {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ERROR, {
        operation: MIXPANEL_OPERATIONS.LOGIN_INTERRUPTED,
        error_data: buildMixpanelErrorData(error, OIDC_PROVIDER.WORKOS),
      });

      if (isOIDCError(error)) {
        handleOIDCError(error);
      } else {
        console.error('Error during SSO login:', error);
        toast.error(t('toasts.fallback.error'));
      }
    }
  };

  return <EmailInputForm type="sso" onSubmit={onSubmit} />;
};

export default SSO;
